<template>
  <v-expansion-panel
    class="mb-4 mt-0"
    data-testid="separator-panel-highlight"
    :class="{ 'is-highlighted': pulse }"
    :disabled="items.length === 0"
  >
    <v-expansion-panel-header>
      <div class="d-flex align-center justify-space-between w-100 pr-4">
        <div>
          <p
            class="checklist-item-title subtitle-2 font-weight-bold grey--text text--darken-3 mb-1"
            data-testid="separator-text-title"
          >
            {{ separator.name }}
          </p>
          <p
            v-if="separator.description"
            v-linkify
            class="ma-0 body-2 grey--text text--darken-2"
          >
            {{ $sanitize(separator.description, { allowedTags: ['a'] }) }}
          </p>
        </div>

        <span
          v-if="showSectionScore"
          class="caption blue-grey--text text--darken-1 text-right"
          data-testid="separator-text-sectionScore"
        >
          {{
            $t('checklist.section_score', {
              points: localizedSectionScorePoints,
              maxPoints: localizedSectionMaxPoints,
            })
          }}

          <span
            class="blue-grey--text text--darken-2 caption font-weight-medium"
            >({{ this.formattedSectionScorePercentage }}%)</span
          >
        </span>
      </div>
    </v-expansion-panel-header>
    <v-expansion-panel-content eager class="px-6 py-3">
      <Item
        v-for="(item, index) in items"
        :data-testid="`routine-item-${item.originalIndex}`"
        :key="item.id"
        :appearancePulseEnabled="childAppearancePulseEnabled(item)"
        :restoreKey="restoreKey"
        :item="item"
        :loading="isLoading"
        :readOnly="readOnly"
        :routineIsCompleted="routineIsCompleted"
        :showDivider="index !== items.length - 1"
        :imageErrors="imageErrors"
        :requestFinding="requestFinding"
        @addImage="$emit('addImage', $event, item.originalIndex)"
        @change="$emit('change', $event, item.originalIndex)"
        @deleteSignature="$emit('deleteSignature', item.originalIndex)"
        @openSignatureDialog="$emit('openSignatureDialog', item.originalIndex)"
        @viewFinding="$emit('viewFinding', item.originalIndex)"
        @handleWarningContinue="
          $emit('handleWarningContinue', $event, item.originalIndex)
        "
        @openCreateFindingModal="$emit('openCreateFindingModal', item)"
      />
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import { utils } from 'frontend-js-shared'

import { formatLocalizedPoints } from '@/lib/formatLocalizedPoints'
import { formatScorePercentage } from '@/lib/formatScorePercentage'

import Item from './Item.vue'

export default {
  name: 'Separator',

  components: {
    Item,
  },

  props: {
    separator: Object,
    readOnly: Boolean,
    routineIsCompleted: Boolean,
    isLoading: Boolean,
    imageErrors: Array,
    requestFinding: Boolean,
    appearancePulseEnabled: {
      type: Boolean,
      default: false,
    },
    restoreKey: {
      type: Number,
      default: 0,
    },
    scoreSummary: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      pulse: false,
    }
  },

  created() {
    // Frozen at mount: the section band plays only for sections that mounted
    // while newly visible. Children derive their own pulse from the live
    // gate at their own mount, so a dependent question appearing later
    // inside an already-visible section still pulses.
    this.pulse =
      this.appearancePulseEnabled &&
      utils.checklists.hasVisibilityRule(this.separator)
  },

  methods: {
    childAppearancePulseEnabled(item) {
      return (
        this.appearancePulseEnabled && utils.checklists.hasVisibilityRule(item)
      )
    },
  },

  computed: {
    items() {
      return this.separator?.items ?? []
    },
    sectionScoreSummary() {
      return this.scoreSummary
    },
    localizedSectionScorePoints() {
      return formatLocalizedPoints(this.sectionScoreSummary?.points ?? 0)
    },
    localizedSectionMaxPoints() {
      return formatLocalizedPoints(this.sectionScoreSummary?.maxPoints ?? 0)
    },
    formattedSectionScorePercentage() {
      return formatScorePercentage(
        this.sectionScoreSummary?.weightedScorePercentage
      )
    },
    showSectionScore() {
      return (
        this.readOnly && this.routineIsCompleted && this.sectionScoreSummary
      )
    },
  },
}
</script>

<style lang="scss" scoped>
// The band anchors on the panel root so a newly appeared section reads as one
// unit: collapsed it covers the header strip, expanded the header plus the
// item list. No margin compensation is needed — the header has no negative
// margins, so the panel box is the band.
.is-highlighted {
  animation: checklist-highlight-pulse 2s ease both;
}

@keyframes checklist-highlight-pulse {
  from {
    background-color: $color-yellow-lighten2;
  }

  to {
    background-color: transparent;
  }
}
</style>
